(() => {
  const scriptElement = document.currentScript;
  const liquidLocalizedRoot = scriptElement?.dataset.storefrontRoot;
  const STOREFRONT_PATH = /^\/(?:products|collections|pages|blogs|policies)(?:\/|$)/;
  const LOCALIZED_STOREFRONT_PATH = /^\/[a-z]{2}(?:-[a-z]{2})?(?=\/(?:products|collections|pages|blogs|policies)(?:\/|$))/i;
  const LOCALIZED_ROOT_PATH = /^\/[a-z]{2}(?:-[a-z]{2})?\/?$/i;

  const normalizeHostname = (hostname) => hostname.toLowerCase().replace(/^www\./, '');

  function getLocalizedRoot() {
    const localizedRoot = liquidLocalizedRoot || window.Shopify?.routes?.root;
    if (typeof localizedRoot !== 'string') return null;

    const trimmedRoot = localizedRoot.replace(/^\/+|\/+$/g, '');
    return trimmedRoot ? `/${trimmedRoot}/` : '/';
  }

  function getStorefrontHostnames() {
    const storefrontHostnames = new Set([normalizeHostname(window.location.hostname)]);
    const canonicalUrl = document.querySelector('link[rel="canonical"]')?.href;

    if (canonicalUrl) {
      try {
        storefrontHostnames.add(normalizeHostname(new URL(canonicalUrl).hostname));
      } catch {
        // Ignore an invalid canonical URL instead of affecting storefront navigation.
      }
    }

    if (window.Shopify?.shop) {
      storefrontHostnames.add(normalizeHostname(window.Shopify.shop));
    }

    return storefrontHostnames;
  }

  function normalizeLink(link, localizedRoot, storefrontHostnames) {
    const href = link.getAttribute('href')?.trim();
    if (
      !href ||
      href.startsWith('#') ||
      link.hasAttribute('hreflang') ||
      link.hasAttribute('data-no-localize') ||
      link.closest('localization-form, .localization-form, [data-localization-form], [data-localization-selector]')
    ) {
      return;
    }

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }

    if (!['http:', 'https:'].includes(url.protocol)) return;
    if (!storefrontHostnames.has(normalizeHostname(url.hostname))) return;

    const storefrontPath = LOCALIZED_ROOT_PATH.test(url.pathname)
      ? '/'
      : url.pathname.replace(LOCALIZED_STOREFRONT_PATH, '');
    if (storefrontPath !== '/' && !STOREFRONT_PATH.test(storefrontPath)) return;

    const localizedPath = storefrontPath === '/'
      ? localizedRoot
      : localizedRoot === '/'
        ? storefrontPath
        : `${localizedRoot}${storefrontPath.slice(1)}`;
    const normalizedHref = `${localizedPath}${url.search}${url.hash}`;

    if (href !== normalizedHref) link.setAttribute('href', normalizedHref);
  }

  function normalizeStorefrontLinks() {
    const localizedRoot = getLocalizedRoot();
    if (!localizedRoot) return;

    const storefrontHostnames = getStorefrontHostnames();

    for (const link of document.querySelectorAll('a[href]')) {
      normalizeLink(link, localizedRoot, storefrontHostnames);
    }
  }

  function observeStorefrontLinks() {
    const localizedRoot = getLocalizedRoot();
    if (!localizedRoot) return;

    const storefrontHostnames = getStorefrontHostnames();
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches('a[href]')) normalizeLink(node, localizedRoot, storefrontHostnames);
          for (const link of node.querySelectorAll('a[href]')) {
            normalizeLink(link, localizedRoot, storefrontHostnames);
          }
        }

        if (mutation.type === 'attributes' && mutation.target instanceof HTMLAnchorElement) {
          normalizeLink(mutation.target, localizedRoot, storefrontHostnames);
        }
      }
    });

    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['href'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', normalizeStorefrontLinks, { once: true });
  } else {
    normalizeStorefrontLinks();
  }

  observeStorefrontLinks();
})();
